const { MinimogThemeStyles, MinimogThemeScripts } = window;

class QuickView {
  constructor() {
    this.modal = new MinimogTheme.Modal();
    this.isOpen = false;
    this.isLoading = false;

    // Event delegation for quick view button
    addEventDelegate({
      selector: ".m-product-quickview-button",
      handler: (e, target) => {
        e.preventDefault();
        if (this.isLoading) return;
        this.target = target;
        this.toggleLoading(true);
        const productHandle = target.dataset.productHandle;
        const variantId = target.dataset.variantId;
        if (productHandle) this.loadProductQuickView(productHandle, variantId);
      },
    });

    // Subscribe to cart update events
    window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
      if (this.modal) this.modal.close();
    });
    window.MinimogEvents.subscribe('ON_MODAL_CLOSED', (modal) => {
      this.close();
      this.unlockPageScroll();
    });

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.rhh-subscription-confirm');
        if (!button) return;
        const modal = button.closest('.rhh-subscription-modal');
        if (!modal) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submitSubscriptionFromModal(button, modal);
      },
      true
    );
  }

  // Load product quick view HTML
  loadProductQuickView(productHandle, variantId) {
    if (this.isOpen) {
      this.modal && this.modal.close(); // Close any existing modal before loading a new one
    }
    this.isLoading = true;
    const productUrl = variantId
      ? `${window.MinimogSettings.base_url}products/${productHandle}?variant=${variantId}`
      : `${window.MinimogSettings.base_url}products/${productHandle}`;
    fetchSection("product-quickview", { url: productUrl })
      .then((html) => {
        this.modalContent = html.querySelector("#MainProduct-quick-view__content");
        const productInfo = html.querySelector("product-info");
        const colorScheme = productInfo.dataset.colorScheme;
        this.modal.appendChild(this.modalContent);
        this.modal.setWidth("960px");

        const sectionWrapper = this.modal.modal.querySelector('#MainProduct-quick-view__content');

        sectionWrapper.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        this.lockPageScroll();
        this.modal.open();
        this.modal.setSizes(`m-gradient ${colorScheme}`);

        this.setupQuickViewLayout(sectionWrapper);
        this.loadHomepageAppScripts(sectionWrapper);

        this.toggleLoading(false);
        this.isOpen = true;
        this.isLoading = false; // Reset loading state
      })
      .then(() => {
        document.dispatchEvent(
          new CustomEvent('tcustomizer-custom-show-quick-view', {
            detail: { productHandle: this.target.dataset.productHandle }
          })
        );

        document.dispatchEvent(
          new CustomEvent("quick-view:loaded", {
            detail: { productUrl: this.target.dataset.productHandle }
          })
        );
      })
      .catch((error) => {
        console.error("Error loading product quick view:", error);
      });
  }

  // Close the quick view modal
  close() {
    this.isOpen = false;
  }

  lockPageScroll() {
    if (this._pageScrollLocked) return;
    const y = window.scrollY || window.pageYOffset || 0;
    this._pageScrollY = y;
    this._pageScrollLocked = true;
    document.documentElement.style.setProperty("--rhh-scroll-lock-top", `-${y}px`);
  }

  unlockPageScroll() {
    if (!this._pageScrollLocked) return;
    const y = this._pageScrollY || 0;
    this._pageScrollLocked = false;
    document.documentElement.style.removeProperty("--rhh-scroll-lock-top");
    window.scrollTo(0, y);
  }

  hideSealWidgets(root) {
    if (!root) return;
    root
      .querySelectorAll(
        "#seal-subscriptions-widget, [id*='seal-subscription'], [class*='sealsubs'], [data-seal-subscriptions]"
      )
      .forEach((node) => {
        node.style.display = "none";
      });
  }

  loadHomepageAppScripts(root) {
    const apps = window.__rhhDeferredApps;
    if (!apps || typeof apps.loadSealAndBundler !== "function") return;
    apps.loadSealAndBundler().then(() => {
      this.hideSealWidgets(root);
      if (this._pageScrollLocked) {
        document.documentElement.style.setProperty("--rhh-scroll-lock-top", `-${this._pageScrollY || 0}px`);
      }
    });
  }

  setupQuickViewLayout(root) {
    if (!root) return;

    const removePaymentSkeletons = () => {
      root.querySelectorAll('.shopify-payment-button__skeleton').forEach((element) => {
        element.remove();
      });
    };

    const cleanupPaymentArtifacts = () => {
      removePaymentSkeletons();

      root.querySelectorAll('.shopify-payment-button__button--hidden').forEach((element) => {
        element.remove();
      });

      root.querySelectorAll('.shopify-payment-button').forEach((wrapper) => {
        wrapper.querySelectorAll('wallet-buttons, shopify-installments').forEach((element) => {
          if (!element.closest('shop-pay-button')) {
            element.remove();
          }
        });
      });

      root.querySelectorAll('.m-product-dynamic-checkout').forEach((checkout) => {
        checkout.querySelectorAll('div, iframe').forEach((element) => {
          const isShopPay = element.matches('shop-pay-button') || element.querySelector('shop-pay-button');
          const isSkeleton = element.classList.contains('shopify-payment-button__skeleton');
          const isMoreOptions =
            element.classList.contains('shopify-payment-button__more-options') && !isSkeleton;
          const isPaymentButton = element.classList.contains('shopify-payment-button');
          if (isShopPay || isMoreOptions || isPaymentButton || isSkeleton) return;

          const rect = element.getBoundingClientRect();
          const isEmpty = !element.textContent.trim() && !element.querySelector('button, shop-pay-button');
          if (isEmpty && rect.height > 0 && rect.height < 48) {
            element.remove();
          }
        });
      });
    };

    const applyLayout = () => {
      cleanupPaymentArtifacts();
      this.hideSealWidgets(root);
    };

    const initSizeScrollShells = () => {
      root.querySelectorAll('.rhh-size-scroll-shell').forEach((shell) => {
        const track = shell.querySelector('.rhh-size-scroll-shell__track');
        const prev = shell.querySelector('.rhh-size-scroll-shell__arrow--prev');
        const next = shell.querySelector('.rhh-size-scroll-shell__arrow--next');
        if (!track || !prev || !next) return;

        const updateArrows = () => {
          const maxScroll = track.scrollWidth - track.clientWidth;
          const canScroll = maxScroll > 4;
          shell.classList.toggle('rhh-size-scroll-shell--scrollable', canScroll);
          prev.hidden = !canScroll || track.scrollLeft <= 2;
          next.hidden = !canScroll || track.scrollLeft >= maxScroll - 2;
        };

        if (shell.dataset.rhhScrollInit !== 'true') {
          shell.dataset.rhhScrollInit = 'true';
          const scrollByAmount = () => Math.max(track.clientWidth * 0.55, 100);

          prev.addEventListener('click', () => {
            track.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' });
          });
          next.addEventListener('click', () => {
            track.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
          });
          track.addEventListener('scroll', updateArrows, { passive: true });
          window.addEventListener('resize', updateArrows);
        }

        updateArrows();
      });
    };

    const revealQuickView = () => {
      root.style.overflow = 'visible';
      root.style.height = 'auto';
      root.style.opacity = '1';
      const wrapper = root.querySelector('.rhh-qv-layout, .m-main-product--wrapper');
      const info = root.querySelector('.m-product-quickview--info');
      if (wrapper) {
        wrapper.style.minHeight = '0';
      }
      if (info) {
        info.style.setProperty('position', 'relative', 'important');
        info.style.setProperty('height', 'auto', 'important');
        info.style.setProperty('overflow', 'visible', 'important');
        info.style.setProperty('opacity', '1', 'important');
      }
      root.querySelectorAll('.m-media-gallery__inner, .swiper-container').forEach((element) => {
        element.style.setProperty('opacity', '1', 'important');
      });
      root.querySelectorAll('responsive-image img').forEach((image) => {
        image.style.setProperty('opacity', '1', 'important');
      });
    };

    applyLayout();
    revealQuickView();
    initSizeScrollShells();
    window.requestAnimationFrame(() => {
      revealQuickView();
      initSizeScrollShells();
    });
    window.setTimeout(() => {
      revealQuickView();
      initSizeScrollShells();
    }, 300);

    const lockHorizontalScroll = () => {
      const nodes = [
        root,
        root.closest('.m-modal--content'),
        root.closest('.m-modal'),
        root.querySelector('.m-main-product--wrapper'),
        root.querySelector('.m-product-quickview--info')
      ];
      nodes.forEach((node) => {
        if (node && node.scrollLeft) node.scrollLeft = 0;
      });
    };

    const keepSelectedSizeInTrack = () => {
      root.querySelectorAll('.rhh-size-scroll-shell__track').forEach((track) => {
        const checked = track.querySelector('input[type="radio"]:checked');
        const node = checked && checked.closest('.m-product-option--node');
        if (!node) return;
        const nodeCenter = node.offsetLeft + node.offsetWidth / 2;
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        track.scrollLeft = Math.min(Math.max(nodeCenter - track.clientWidth / 2, 0), maxScroll);
      });
    };

    const stabilizeVariantLayout = () => {
      if (!root.isConnected) return;
      lockHorizontalScroll();
      keepSelectedSizeInTrack();
      initSizeScrollShells();
    };

    root.addEventListener(
      'focusin',
      (event) => {
        lockHorizontalScroll();
        if (event.target.closest('variant-picker')) {
          keepSelectedSizeInTrack();
          initSizeScrollShells();
        }
      },
      true
    );

    root.addEventListener('change', (event) => {
      if (!event.target.closest('variant-picker')) return;
      stabilizeVariantLayout();
      window.requestAnimationFrame(stabilizeVariantLayout);
    });

    const onVariantUpdated = () => {
      stabilizeVariantLayout();
      window.requestAnimationFrame(stabilizeVariantLayout);
      window.setTimeout(stabilizeVariantLayout, 50);
      window.setTimeout(stabilizeVariantLayout, 200);
    };

    document.addEventListener('variant:changed', onVariantUpdated);
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.variantChange, onVariantUpdated);
    }

    const variantHost = root.querySelector('.rhh-product-variant-atf') || root;
    let variantUiTimer;
    const variantUiObserver = new MutationObserver(() => {
      window.clearTimeout(variantUiTimer);
      variantUiTimer = window.setTimeout(onVariantUpdated, 40);
    });
    variantUiObserver.observe(variantHost, { childList: true, subtree: true });

    const checkout = root.querySelector('.m-product-dynamic-checkout');
    if (checkout) {
      const observer = new MutationObserver(applyLayout);
      observer.observe(checkout, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 8000);
    }
  }

  submitSubscriptionFromModal(button, modal) {
    if (!button || button.dataset.rhhSubmitting === 'true') return;

    const selectedPlan = modal.querySelector('.rhh-frequency-list__option.is-selected');
    const planInput = modal.querySelector('input[id^="modal-frequency-value-"]');
    const sellingPlanId =
      (selectedPlan && selectedPlan.getAttribute('data-plan-id')) ||
      (planInput && planInput.value) ||
      button.getAttribute('data-rhh-selling-plan');

    const quickView = document.getElementById('MainProduct-quick-view__content');
    const form =
      (quickView && quickView.querySelector('form[data-type="add-to-cart-form"]')) ||
      document.querySelector('.m-product-form--main form[data-type="add-to-cart-form"]');

    let variantId = '';
    const idInputs = [
      ...(form ? form.querySelectorAll('[name="id"]') : []),
      ...(quickView ? quickView.querySelectorAll('[name="id"]') : [])
    ];
    const filledId = idInputs.find((input) => input && String(input.value || '').trim());
    if (filledId) variantId = String(filledId.value).trim();
    if (!variantId) variantId = button.getAttribute('data-rhh-variant-id') || '';

    const qtyInput = form && form.querySelector('[name="quantity"]');
    const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

    if (!variantId || !sellingPlanId) return;

    const MinimogSettings = window.MinimogSettings || {};
    const MinimogStrings = window.MinimogStrings || {};
    const cartAddUrl =
      (MinimogSettings.routes && MinimogSettings.routes.cart_add_url) || '/cart/add.js';
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    button.dataset.rhhSubmitting = 'true';
    button.disabled = true;
    button.classList.add('m-spinner-loading');

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: variantId,
        quantity,
        selling_plan: sellingPlanId,
        sections: sectionsToBundle,
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json())
      .then((response) => {
        if (response && response.status) {
          throw new Error(response.description || response.message || 'Unable to add subscription.');
        }

        modal.style.display = 'none';
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('rhh-subscription-modal-open');

        const cartDrawer = document.querySelector('m-cart-drawer');
        if (MinimogSettings.use_ajax_atc && cartDrawer && MinimogSettings.enable_cart_drawer) {
          if (window.MinimogTheme && window.MinimogTheme.Notification) {
            window.MinimogTheme.Notification.show({
              target: cartDrawer.querySelector('m-cart-items'),
              method: 'prepend',
              type: 'success',
              message: MinimogStrings.itemAdded,
              delay: 400
            });
          }
          cartDrawer.open();
        }

        if (window.MinimogEvents && window.MinimogTheme) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
            cart: response,
            productVariantId: variantId
          });
        }
      })
      .catch((error) => {
        if (window.MinimogTheme && window.MinimogTheme.Notification) {
          window.MinimogTheme.Notification.show({
            target: document.body,
            method: 'appendChild',
            type: 'error',
            message: (error && error.message) || 'Unable to add subscription.',
            last: 4000,
            sticky: true
          });
        }
      })
      .finally(() => {
        button.dataset.rhhSubmitting = 'false';
        button.disabled = false;
        button.classList.remove('m-spinner-loading');
      });
  }

  // Toggle loading spinner on the target element
  toggleLoading(isLoading) {
    if (isLoading) {
      this.target.classList.add("m-spinner-loading");
    } else {
      this.target.classList.remove("m-spinner-loading");
    }
  }
}

// Initialize the QuickView instance
MinimogTheme.ProductQuickView = new QuickView();
